import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
  FaQuestionCircle,
  FaUser,
  FaSortNumericDown,
} from "react-icons/fa";

import {
  useGetQuizQuestionsQuery,
  useDeleteQuizQuestionMutation,
} from "../../slices/quizQuestionApiSlice";

const QuizQuestionListScreen = () => {
  const navigate = useNavigate();

  const { quizId } = useParams();

  const {
    data: quizQuestions,
    isLoading,
    error,
    refetch,
  } = useGetQuizQuestionsQuery(quizId);

  const [deleteQuizQuestion, { isLoading: loadingDelete }] =
    useDeleteQuizQuestionMutation();

  const deleteHandler = async (id) => {
    if (
      window.confirm("Are you sure you want to remove this question from quiz?")
    ) {
      try {
        await deleteQuizQuestion(id).unwrap();

        toast.success("Question removed from quiz successfully");

        refetch();
      } catch (err) {
        toast.error(
          err?.data?.message || err?.error || "Failed to remove question",
        );
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-lg text-gray-600">
        Loading quiz questions...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="rounded-xl bg-red-100 p-5 text-red-700 shadow-lg">
          {error?.data?.message ||
            error?.error ||
            "Failed to load quiz questions"}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-800">Quiz Questions</h1>

            <p className="mt-2 text-gray-500">
              Manage questions assigned to this quiz
            </p>
          </div>

          <Link
            to={`/quiz-questions/create?quiz=${quizId}`}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 font-semibold text-white shadow-lg hover:from-blue-700 hover:to-indigo-700 transition"
          >
            <FaPlus />
            Add Question
          </Link>
        </div>

        {loadingDelete && (
          <div className="mb-6 rounded-xl bg-blue-100 p-4 text-center text-blue-700">
            Removing question...
          </div>
        )}

        {quizQuestions?.length === 0 ? (
          <div className="rounded-3xl bg-white p-12 text-center shadow-xl">
            <FaQuestionCircle className="mx-auto mb-4 text-6xl text-blue-600" />

            <h2 className="text-2xl font-bold text-gray-700">
              No Questions Added
            </h2>

            <p className="mt-3 text-gray-500">
              Add questions to this quiz to start building your test.
            </p>

            <Link
              to={`/quiz-questions/create?quiz=${quizId}`}
              className="inline-flex items-center gap-2 mt-6 rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white hover:bg-blue-700 transition"
            >
              <FaPlus />
              Add Question
            </Link>
          </div>
        ) : (
          <div className="grid gap-6">
            {quizQuestions?.map((item, index) => (
              <div
                key={item._id}
                className="rounded-3xl bg-white/90 backdrop-blur border border-gray-100 shadow-xl hover:shadow-2xl transition p-6"
              >
                <div className="flex flex-col lg:flex-row lg:justify-between gap-6">
                  {/* Question Details */}

                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h2 className="text-xl font-bold text-gray-800">
                          Question #{index + 1}
                        </h2>

                        <p className="mt-2 text-gray-600 leading-relaxed">
                          {item.question?.questionText ||
                            item.question?.text ||
                            item.question?.title ||
                            "Question details unavailable"}
                        </p>
                      </div>

                      <span className="rounded-full bg-indigo-100 px-4 py-1 text-sm font-semibold text-indigo-700">
                        Active
                      </span>
                    </div>

                    <div className="grid md:grid-cols-3 gap-5 mt-6">
                      <div className="flex items-center gap-3">
                        <FaSortNumericDown className="text-blue-600 text-xl" />

                        <div>
                          <p className="text-xs uppercase text-gray-500">
                            Order
                          </p>

                          <p className="font-semibold">{item.order}</p>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs uppercase text-gray-500">Marks</p>

                        <p className="font-semibold">{item.marks}</p>
                      </div>

                      <div className="flex items-center gap-3">
                        <FaUser className="text-green-600" />

                        <div>
                          <p className="text-xs uppercase text-gray-500">
                            Created By
                          </p>

                          <p className="font-semibold">
                            {item.createdBy?.name || "-"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}

                  <div className="flex lg:flex-col gap-3 lg:w-44">
                    <button
                      onClick={() => navigate(`/quiz-questions/${item._id}`)}
                      className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-white hover:bg-blue-700 transition"
                    >
                      <FaEye />
                      View
                    </button>

                    <button
                      onClick={() =>
                        navigate(`/quiz-questions/${item._id}/edit`)
                      }
                      className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 py-3 text-white hover:bg-amber-600 transition"
                    >
                      <FaEdit />
                      Edit
                    </button>

                    <button
                      onClick={() => deleteHandler(item._id)}
                      className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-3 text-white hover:bg-red-700 transition"
                    >
                      <FaTrash />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizQuestionListScreen;
